import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { useSearchParams } from 'react-router-dom'
import { useAcceptTerms } from 'pages/privacy-policy-acceptance/hooks/useAcceptTerms'
import { useRequestTermsPendingAcceptance } from 'pages/privacy-policy-acceptance/hooks/useRequestTermsPendingAcceptance'
import { Routes } from 'common/constants'
import { logBridgeError, sendSelectContent } from 'common'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { TTerm } from 'pages/privacy-policy/types/useRequestTerms.types'
import { TEntity } from 'contexts/app-context/types'

import { interWbNavigate } from '@interco/inter-webview-bridge'

import { TCheckBoxState } from '../types'
import { useGetPrivacyPolicyAcceptanteContent } from './useGetPrivacyPolicyAcceptanteContent'

type CheckBoxAction =
  | { type: 'TOGGLE_ITEM'; payload: { versionId: string } }
  | { type: 'INITIALIZE_ITEMS'; payload: { terms: TTerm[] } }
  | { type: 'RESET' }

interface ICheckBoxReducerState extends TCheckBoxState {
  checkedCount: number
  totalCount: number
}

const EMPTY_TERMS: TTerm[] = []

const checkBoxReducer = (
  state: ICheckBoxReducerState,
  action: CheckBoxAction,
): ICheckBoxReducerState => {
  switch (action.type) {
    case 'TOGGLE_ITEM': {
      const { versionId } = action.payload
      const currentValue = state.items[versionId]

      if (typeof currentValue !== 'boolean') return state

      const nextValue = !currentValue
      const checkedCount = state.checkedCount + (nextValue ? 1 : -1)
      const newItems = {
        ...state.items,
        [versionId]: nextValue,
      }

      return {
        items: newItems,
        checkedCount,
        totalCount: state.totalCount,
        isAllChecked: state.totalCount > 0 && checkedCount === state.totalCount,
      }
    }

    case 'INITIALIZE_ITEMS': {
      const items: Record<string, boolean> = {}

      for (const term of action.payload.terms) {
        items[term.versionId] = false
      }

      const totalCount = action.payload.terms.length

      return { items, isAllChecked: false, checkedCount: 0, totalCount }
    }
    case 'RESET':
      return { items: {}, isAllChecked: false, checkedCount: 0, totalCount: 0 }
    default:
      return state
  }
}

export const usePrivacyPolicyAcceptance = () => {
  const [searchParams] = useSearchParams()
  const [checkBoxState, dispatch] = useReducer(checkBoxReducer, {
    items: {},
    isAllChecked: false,
    checkedCount: 0,
    totalCount: 0,
  })

  const org = searchParams.get('org')

  const termsVersionKeyRef = useRef('')

  const { content, endpoints, deeplinks, isErrorContent, isLoadingContent } =
    useGetPrivacyPolicyAcceptanteContent(org as TEntity)

  const {
    terms,
    isError: isErrorPending,
    isLoading: isLoadingPending,
  } = useRequestTermsPendingAcceptance(org as TEntity)

  const { acceptTermsFn, isAcceptTermsLoading, isAcceptTermsError, isAcceptTermsSuccess } =
    useAcceptTerms({ terms }, endpoints?.acceptTerms)

  const isError = isErrorContent || isErrorPending || isAcceptTermsError
  const isLoading = isLoadingContent || isAcceptTermsLoading
  const processedTerms = terms ?? EMPTY_TERMS
  const isEmpty = !isLoadingPending && processedTerms.length === 0

  const contentValues = useMemo(
    () => ({
      title: content?.title ?? '',
      menuItemIconRef: content?.menuItemIconRef ?? '',
      checkBoxCommonText: content?.checkBoxCommonText ?? '',
      successFeedbackPage: content?.successFeedbackPage,
      errorFeedbackPage: content?.errorFeedbackPage,
      emptyPage: content?.emptyPage,
      confirmButtonText: content?.confirmButtonText ?? '',
    }),
    [content],
  )

  const checkBoxHandler = useCallback(async (versionId: string) => {
    try {
      await sendSelectContent({
        flow: 'privacy_policy_acceptance',
        step: 'privacy_policy_acceptance',
        screen_name: Routes.PRIVACY_POLICY_ACCEPTANCE,
        element_name: 'checkPolicy',
        element_type: 'checkbox',
        parent_name: 'PrivacyPolicyAcceptance',
        scenario: JSON.stringify({
          screen: Routes.PRIVACY_POLICY_ACCEPTANCE,
          versionId,
          ref_type: 'checkbox',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send privacy policy acceptance checkbox tic', error)
    }

    dispatch({ type: 'TOGGLE_ITEM', payload: { versionId } })
  }, [])

  const menuItemButtonHandler = useCallback(async (url: string, title: string) => {
    try {
      await sendSelectContent({
        flow: 'privacy_policy_acceptance',
        step: 'privacy_policy_acceptance',
        screen_name: Routes.PRIVACY_POLICY_ACCEPTANCE,
        element_name: 'verifyPolicy',
        element_type: 'checkbox',
        parent_name: 'PrivacyPolicyAcceptance',
        scenario: JSON.stringify({
          screen: Routes.PRIVACY_POLICY_ACCEPTANCE,
          url,
          title,
          ref_type: 'checkbox',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send privacy policy acceptance view policy tic', error)
    }

    interWbNavigate.openPdf(title, url)
  }, [])

  const acceptanceHandler = useCallback(async () => {
    try {
      await sendSelectContent({
        flow: 'privacy_policy_acceptance',
        step: 'privacy_policy_acceptance',
        screen_name: Routes.PRIVACY_POLICY_ACCEPTANCE,
        element_name: 'acceptPolicyTerms',
        element_type: 'checkbox',
        parent_name: 'PrivacyPolicyAcceptance',
        scenario: JSON.stringify({
          screen: Routes.PRIVACY_POLICY_ACCEPTANCE,
          ref_type: 'checkbox',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send privacy policy acceptance confirm tic', error)
    }

    if (checkBoxState.isAllChecked) {
      acceptTermsFn(org as TEntity)
    }
  }, [checkBoxState.isAllChecked, acceptTermsFn, org])

  const onClickLeftIcon = useCallback(() => {
    if (deeplinks?.home) {
      interWbNavigate.openDeepLink(deeplinks.home)
    }
  }, [deeplinks?.home])

  const confirmButton = useMemo(
    () => ({
      text: contentValues.confirmButtonText,
      isLoading: isAcceptTermsLoading,
      isDisabled: !checkBoxState.isAllChecked,
      handler: acceptanceHandler,
    }),
    [
      contentValues.confirmButtonText,
      isAcceptTermsLoading,
      checkBoxState.isAllChecked,
      acceptanceHandler,
    ],
  )

  useEffect(() => {
    const currentTermsVersionKey = processedTerms.map(({ versionId }) => versionId).join('|')

    if (termsVersionKeyRef.current === currentTermsVersionKey) return

    termsVersionKeyRef.current = currentTermsVersionKey

    if (processedTerms.length === 0) {
      dispatch({ type: 'RESET' })
      return
    }

    dispatch({
      type: 'INITIALIZE_ITEMS',
      payload: { terms: processedTerms },
    })
  }, [processedTerms])

  useScreenFinishLoadTac({
    name: 'PrivacyPolicyAcceptance',
    entity: undefined,
    action: 'PrivacyPolicyAcceptanceFinishLoad',
    screen: Routes.PRIVACY_POLICY_ACCEPTANCE,
    shouldSend: !isLoading && !isError,
    logLabel: 'send privacy policy acceptance finish tac',
  })

  return {
    isLoading,
    isAcceptTermsError,
    isError,
    isLoadingPending,
    isSuccess: isAcceptTermsSuccess,
    ...contentValues,
    checkBoxState,
    confirmButton,
    terms: processedTerms,
    deeplinks,
    isEmpty,
    checkBoxHandler,
    menuItemButtonHandler,
    onClickLeftIcon,
  }
}
